import {Component, ElementRef, Inject, LOCALE_ID, ViewChild} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {localizeRole, Role} from '../../model/auth/Role';
import {ProgressService} from '../../service/progress.service';
import {BaseModalComponent} from '../../common/modal/base-modal/base-modal.component';
import {NotificationHandler} from '../../helper/notification.handler';
import {Asset, AssetType} from '../../model/asset/Asset';
import {AssetService} from '../../service/asset.service';
import {School} from '../../model/location/School';
import {Region} from '../../model/location/Region';
import {UserService} from '../../service/user.service';
import {Locale} from '../../model/Locale';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {localizeUserImportIssue, User, UserImportIssue, UserImportResult} from '../../model/auth/User';
import {saveAs} from 'file-saver';

export interface ImportUsersModalData {
  competitionId: string;
  contestId: string;
  school: School;
  region: Region;
}

@Component({
  selector: 'app-import-users-modal',
  templateUrl: './import-users-modal.component.html',
  styleUrls: ['./import-users-modal.component.scss'],
})
export class ImportUsersModalComponent extends BaseModalComponent {

  readonly roles = [Role.Host, Role.Coordinator, Role.Teacher];

  @ViewChild('dialogContent', {static: true}) protected progressContainer!: ElementRef;

  file?: File;

  readonly contestId?: string;
  readonly competitionId?: string;

  readonly region?: Region;
  readonly school?: School;

  readonly locale: Locale;

  importResult?: UserImportResult;

  readonly userRoles: Role[] = [Role.Student];
  roleField = new FormControl({value: '', disabled: true}, [
    Validators.required
  ]);
  importForm = new FormGroup({
    role: this.roleField,
  });

  constructor(private assetService: AssetService,
              private userService: UserService,
              private dialog: MatDialog,
              dialogRef: MatDialogRef<ImportUsersModalComponent>,
              progressService: ProgressService,
              @Inject(LOCALE_ID) locale: string,
              @Inject(MAT_DIALOG_DATA) data: ImportUsersModalData) {
    super(dialogRef, progressService);

    this.contestId = data.contestId;
    this.competitionId = data.competitionId;
    this.region = data.region;
    this.school = data.school;

    this.locale = locale as Locale;

    this.roleField.setValue(Role.Student);
  }

  compareRoles(r1: Role, r2: Role) {
    return r1 == r2;
  }

  displayRole(role: Role) {
    return localizeRole(role);
  }

  get exampleFileUrl(): string {
    return this.locale == Locale.en
      ? 'assets/examples/users-import.csv'
      : 'assets/examples/users-import-uk.csv';
  }

  get hasImportIssues(): boolean {
    return this.importResult != undefined && Object.keys(this.importResult.issues || {}).length > 0;
  }

  localizeImportIssue(issue: string): string {
    return localizeUserImportIssue(issue as UserImportIssue);
  }

  onFileSelected(files: File[]) {
    this.file = files && files.length > 0 ? files[0] : undefined;
  }

  proceedAllowed(): boolean {
    return !!this.file && !!this.school && !!this.region && !!this.competitionId && !!this.contestId;
  }

  proceed(): void {
    if (!this.contestId || !this.competitionId || !this.file) return;

    this.startProgress();

    this.registerAssetUpload(this.file);
  }

  private registerAssetUpload(file: File): void {
    this.assetService.registerAssetUpload(AssetType.users_import, file).subscribe(asset => {
      this.uploadAsset(asset);
    }, error => {
      NotificationHandler.handleError($localize`:Notifications|Asset upload registration failed message@@notifications-asset-upload-registration-failed-error:Asset upload registration has failed`, error);
      this.stopProgress();
    });
  }

  private uploadAsset(asset: Asset) {
    if (!asset.uploadUrl || !this.file) return;

    this.assetService.uploadContent(asset.uploadUrl, this.file).subscribe({
      next: () => {
        this.importUsers(asset.url || '');
      },
      error: error => {
        NotificationHandler.handleError($localize`:Notifications|Asset upload failed message@@notifications-asset-upload-failed-error:Asset upload has failed`, error);
        this.stopProgress();
      }
    });
  }

  private importUsers(url: string) {
    const region = this.region?.id as string;
    const school = this.school?.id as string;
    const contestId = this.contestId as string;
    const competitionId = this.competitionId as string;

    this.userService.importUsers(url, Role.Student, region, school, contestId, competitionId, this.locale).subscribe({
      next: result => {
        this.importResult = result;
        this.stopProgress();
      },
      error: error => {
        NotificationHandler.handleError($localize`:Notifications|Users import failed message@@notifications-users-import-failed-error:Users import has failed`, error);
        this.stopProgress();
      }
    });
  }

  downloadFile(users: User[], issue: string) {
    const replacer = (key: string, value: string) => value || ''; // specify how you want to handle null values here
    const header = ['first_name', 'last_name', 'email', 'grade'];

    const csv = users
      .map(user => [
        user.firstName,
        user.lastName,
        user.email,
        user.schoolGrade
      ]
        .map(value => JSON.stringify(value, replacer))
        .join(','));

    csv.unshift(header.join(','));

    const csvArray = csv.join('\r\n');

    const blob = new Blob([csvArray], {type: 'text/csv'});

    saveAs(blob, `users-access-${issue}.csv`);
  }
}
